/**
 * MCP Configuration Panel - per-conversation tool configuration.
 *
 * Uses the unified ToolAccessTable for module configuration.
 * Wraps in a popover triggered by a settings icon button.
 * Also includes web search toggle and external MCP server badges.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Settings2, Server, Wrench, Info, Globe, Link as LinkIcon, Navigation } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { DialogForm } from '../common';
import { Separator } from '../../../components/ui/separator';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../api/axiosInstance';
import { ToolAccessTable } from './ToolAccessTable';
import { normalizeLegacyMcpModuleName } from './mcpModuleMapping';
import type { ToolAccessModule } from './ToolAccessTable';
import type { McpConfig, McpAccessLevel, ToolAccessRow } from './types';

/** Tool metadata from /api/v1/mcp/modules/{name} */
interface McpTool {
  name: string;
  description?: string;
  method?: string;
}

/** External MCP server from tenant config */
interface ExternalMcpServer {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  tools?: Array<{ name: string; description?: string }>;
}

/** HTTP methods that are considered write operations */
const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

interface McpConfigPanelProps {
  /** Current MCP configuration */
  config: McpConfig;
  /** Called when configuration changes */
  onChange: (config: McpConfig) => void;
  /** Whether the panel is read-only */
  readOnly?: boolean;
  /** Show "Agent's own" data scope option (agent chat context) */
  showAgentScope?: boolean;
  /** Show the "Whose Data" column */
  showWhoseData?: boolean;
  /** Current user info for scope display */
  currentUser?: { id: string; name: string };
  /** Whether current user is admin */
  isAdmin?: boolean;
  /** Pre-populated rows from agent config (shown with "inherited" badge) */
  inheritedRows?: ToolAccessRow[];
}

/** Convert McpConfig to ToolAccessRow[] for display */
export function configToRows(
  config: McpConfig,
  inheritedRows?: ToolAccessRow[],
  currentUser?: { id: string; name: string },
): ToolAccessRow[] {
  // Normalize rows persisted while nested discovery names leaked into runtime
  // config. Prefer an explicit canonical row when both forms are present.
  if (config.toolAccessRows && config.toolAccessRows.length > 0) {
    const normalizedRows: ToolAccessRow[] = [];
    for (const row of config.toolAccessRows) {
      const moduleName = normalizeLegacyMcpModuleName(row.module_name);
      const normalizedRow = { ...row, module_name: moduleName };
      const existingIndex = normalizedRows.findIndex(
        candidate => candidate.module_name === moduleName,
      );
      if (existingIndex === -1) {
        normalizedRows.push(normalizedRow);
      } else if (row.module_name === moduleName) {
        normalizedRows[existingIndex] = normalizedRow;
      }
    }
    return normalizedRows;
  }

  // Convert from legacy enabledModules + moduleAccess format
  const rows: ToolAccessRow[] = [];
  const inheritedModules = new Set((inheritedRows || []).map((r) => r.module_name));

  for (const rawModuleName of config.enabledModules || []) {
    // Skip external server entries
    if (rawModuleName.startsWith('external__')) continue;

    // Sessions saved while master_data's MCP modules were registered under
    // dotted keys carry 'master_data.products'/'master_data.contacts' —
    // normalize so they match the current module list (WI 0ba39ef9).
    const moduleName = normalizeLegacyMcpModuleName(rawModuleName);
    if (rows.some((r) => r.module_name === moduleName)) continue;

    const accessLevel =
      config.moduleAccess?.[moduleName] || config.moduleAccess?.[rawModuleName] || 'read';
    const isInherited = inheritedModules.has(moduleName);

    rows.push({
      id: `legacy_${moduleName}`,
      module_name: moduleName,
      access_level: accessLevel,
      data_scope: 'user',
      scope_user_id: currentUser?.id,
      scope_user_name: currentUser?.name,
      inherited: isInherited,
    });
  }

  // Add inherited rows that aren't already in the config
  for (const inherited of inheritedRows || []) {
    if (!rows.some((r) => r.module_name === inherited.module_name)) {
      rows.push({ ...inherited, inherited: true });
    }
  }

  return rows;
}

/** Convert ToolAccessRow[] back to McpConfig fields */
export function rowsToConfig(
  rows: ToolAccessRow[],
  existingConfig: McpConfig,
): Partial<McpConfig> {
  const canonicalRows = rows.map(row => ({
    ...row,
    module_name: normalizeLegacyMcpModuleName(row.module_name),
  }));
  const enabledModules = [...new Set(canonicalRows.map((r) => r.module_name))];
  const moduleAccess: Record<string, McpAccessLevel> = {};

  for (const row of canonicalRows) {
    moduleAccess[row.module_name] = row.access_level;
  }

  // Preserve external server entries
  for (const mod of existingConfig.enabledModules || []) {
    if (mod.startsWith('external__') && !enabledModules.includes(mod)) {
      enabledModules.push(mod);
    }
  }

  return {
    enabledModules,
    moduleAccess,
    toolAccessRows: canonicalRows,
  };
}

export const McpConfigPanel: React.FC<McpConfigPanelProps> = ({
  config,
  onChange,
  readOnly = false,
  showAgentScope = false,
  showWhoseData = false,
  currentUser,
  isAdmin = false,
  inheritedRows,
}) => {
  const { t } = useTranslation('aiChat');
  const [open, setOpen] = useState(false);
  const [toolDialogModule, setToolDialogModule] = useState<ToolAccessModule | null>(null);

  // Fetch available PanLuma MCP modules
  const { data: modulesData } = useQuery({
    queryKey: ['mcp-modules'],
    queryFn: async (): Promise<ToolAccessModule[]> => {
      const res = await axiosInstance.get('/api/v1/mcp/modules');
      return res.data?.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch external MCP servers
  const { data: externalServers } = useQuery({
    queryKey: ['mcp-servers', { includeInactive: false }],
    queryFn: async (): Promise<ExternalMcpServer[]> => {
      const res = await axiosInstance.get('/api/v1/admin/mcp-servers', {
        params: { include_inactive: false },
      });
      return res.data?.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const modules = useMemo(() => modulesData || [], [modulesData]);
  const servers = useMemo(() => externalServers || [], [externalServers]);

  const webSearchEnabled = config.webSearchEnabled !== false; // default true
  const webFetchEnabled = config.webFetchEnabled !== false; // default true
  const webNavigateEnabled = config.webNavigateEnabled === true; // default false

  const toggleWebSearch = useCallback(() => {
    if (readOnly) return;
    onChange({ ...config, webSearchEnabled: !webSearchEnabled });
  }, [config, onChange, readOnly, webSearchEnabled]);

  const toggleWebFetch = useCallback(() => {
    if (readOnly) return;
    onChange({ ...config, webFetchEnabled: !webFetchEnabled });
  }, [config, onChange, readOnly, webFetchEnabled]);

  const toggleWebNavigate = useCallback(() => {
    if (readOnly) return;
    onChange({ ...config, webNavigateEnabled: !webNavigateEnabled });
  }, [config, onChange, readOnly, webNavigateEnabled]);

  // Convert McpConfig ↔ ToolAccessRow[]
  const rows = useMemo(
    () => configToRows(config, inheritedRows, currentUser),
    [config, inheritedRows, currentUser],
  );

  const handleRowsChange = useCallback(
    (newRows: ToolAccessRow[]) => {
      const updates = rowsToConfig(newRows, config);
      onChange({ ...config, ...updates });
    },
    [config, onChange],
  );

  // External server toggle
  const enabledSet = useMemo(
    () => new Set(config.enabledModules || []),
    [config.enabledModules],
  );

  const toggleExternalServer = useCallback(
    (serverKey: string) => {
      if (readOnly) return;
      const current = config.enabledModules || [];
      const updated = current.includes(serverKey)
        ? current.filter((m) => m !== serverKey)
        : [...current, serverKey];
      onChange({ ...config, enabledModules: updated });
    },
    [config, onChange, readOnly],
  );

  // Per-tool toggle
  const disabledToolsSet = useMemo(
    () => new Set(config.disabledTools || []),
    [config.disabledTools],
  );

  const toggleTool = useCallback(
    (toolKey: string) => {
      if (readOnly) return;
      const disabled = config.disabledTools || [];
      const updated = disabled.includes(toolKey)
        ? disabled.filter((t) => t !== toolKey)
        : [...disabled, toolKey];
      onChange({ ...config, disabledTools: updated });
    },
    [config, onChange, readOnly],
  );

  // Count enabled PanLuma modules + external MCP servers
  const enabledExternalCount = (config.enabledModules || []).filter((m) =>
    m.startsWith('external__'),
  ).length;
  const enabledModuleCount = rows.length + enabledExternalCount;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground relative"
          >
            <Settings2 className="h-3.5 w-3.5" />
            {enabledModuleCount > 0 && (
              <Badge
                variant="secondary"
                className="absolute -top-0.5 -right-0.5 text-[9px] h-3.5 min-w-3.5 px-0.5 flex items-center justify-center rounded-full"
              >
                {enabledModuleCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[480px] p-0 flex flex-col"
          align="start"
          side="top"
          collisionPadding={16}
          style={{
            maxHeight: 'var(--radix-popover-content-available-height, 400px)',
          }}
        >
          <div className="p-3 pb-2 shrink-0">
            <h4 className="font-medium text-sm flex items-center gap-1.5">
              <Wrench className="h-4 w-4" />
              {t('mcp.toolConfiguration')}
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('mcp.description')}
            </p>
          </div>

          <Separator className="shrink-0" />

          <ScrollArea className="flex-1 min-h-0">
            <div className="p-3 space-y-3">
              {/* Web Search toggle */}
              <div className="flex items-center justify-between gap-2 py-1">
                <div className="flex items-center gap-1.5">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs">{t('mcp.webSearch')}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-48">
                      <p className="text-xs">{t('mcp.webSearchTooltip')}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Switch
                  checked={webSearchEnabled}
                  onCheckedChange={toggleWebSearch}
                  disabled={readOnly}
                />
              </div>

              {/* Web Fetch toggle */}
              <div className="flex items-center justify-between gap-2 py-1">
                <div className="flex items-center gap-1.5">
                  <LinkIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs">{t('mcp.webFetch')}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-48">
                      <p className="text-xs">{t('mcp.webFetchTooltip')}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Switch
                  checked={webFetchEnabled}
                  onCheckedChange={toggleWebFetch}
                  disabled={readOnly}
                />
              </div>

              {/* Web Navigate toggle */}
              <div className="flex items-center justify-between gap-2 py-1">
                <div className="flex items-center gap-1.5">
                  <Navigation className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs">{t('mcp.webNavigate', 'Web Navigate')}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-48">
                      <p className="text-xs">{t('mcp.webNavigateTooltip', 'Interactive browser navigation for JavaScript-heavy sites. Uses more resources than Web Fetch.')}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Switch
                  checked={webNavigateEnabled}
                  onCheckedChange={toggleWebNavigate}
                  disabled={readOnly}
                />
              </div>

              <Separator />

              {/* PanLuma Modules — unified tool access table */}
              <ToolAccessTable
                rows={rows}
                onChange={handleRowsChange}
                availableModules={modules}
                showAgentScope={showAgentScope}
                showWhoseData={showWhoseData}
                currentUser={currentUser}
                isAdmin={isAdmin}
                readOnly={readOnly}
                compact
              />

              {/* External MCP Servers */}
              {servers.length > 0 && (
                <div>
                  <Separator className="mb-3" />
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    <Server className="h-3 w-3 inline mr-1" />
                    {t('mcp.externalServers')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {servers.map((server) => {
                      const serverKey = `external__${server.id}`;
                      const isEnabled = enabledSet.has(serverKey);
                      return (
                        <Badge
                          key={server.id}
                          variant={isEnabled ? 'default' : 'outline'}
                          className={cn(
                            'cursor-pointer text-xs transition-colors',
                            readOnly && 'cursor-default',
                            isEnabled
                              ? 'bg-info text-info-foreground hover:bg-info/90'
                              : 'hover:bg-muted text-muted-foreground',
                          )}
                          onClick={() => toggleExternalServer(serverKey)}
                          title={server.description}
                        >
                          {server.name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Per-tool configuration dialog */}
      {toolDialogModule && (
        <ToolConfigDialog
          module={toolDialogModule}
          open={!!toolDialogModule}
          onOpenChange={(dialogOpen) => {
            if (!dialogOpen) setToolDialogModule(null);
          }}
          disabledTools={disabledToolsSet}
          onToggleTool={toggleTool}
          readOnly={readOnly}
        />
      )}
    </>
  );
};

/** Format a tool name for display (remove API path suffix, humanize) */
function formatToolName(name: string): string {
  const apiIdx = name.indexOf('_api_v1_');
  const displayName = apiIdx > 0 ? name.substring(0, apiIdx) : name;
  return displayName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Method badge color */
function getMethodColor(method: string): string {
  switch (method.toLowerCase()) {
    case 'get':
      return 'bg-info/10 text-info';
    case 'post':
      return 'bg-success/10 text-success';
    case 'put':
    case 'patch':
      // No distinct token for orange; PATCH shares the warning hue with PUT.
      return 'bg-warning/10 text-warning';
    case 'delete':
      return 'bg-destructive/10 text-destructive';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/** Dialog showing individual tools for a module with on/off switches */
const ToolConfigDialog: React.FC<{
  module: ToolAccessModule;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabledTools: Set<string>;
  onToggleTool: (toolKey: string) => void;
  readOnly: boolean;
}> = ({ module: mod, open, onOpenChange, disabledTools, onToggleTool, readOnly }) => {
  const { t } = useTranslation('aiChat');

  // Fetch tools for this module
  const { data: tools, isLoading } = useQuery({
    queryKey: ['mcp-module-tools', mod.name],
    queryFn: async (): Promise<McpTool[]> => {
      const res = await axiosInstance.get(`/api/v1/mcp/modules/${mod.name}`);
      return res.data?.data?.tools || [];
    },
    staleTime: 10 * 60 * 1000,
    enabled: open,
  });

  // Separate read and write tools
  const { readTools, writeTools } = useMemo(() => {
    if (!tools) return { readTools: [], writeTools: [] };
    const read: McpTool[] = [];
    const write: McpTool[] = [];
    for (const tool of tools) {
      if (tool.method && WRITE_METHODS.has(tool.method.toLowerCase())) {
        write.push(tool);
      } else {
        read.push(tool);
      }
    }
    return { readTools: read, writeTools: write };
  }, [tools]);

  return (
    <DialogForm
      open={open}
      onClose={() => onOpenChange(false)}
      title={t('mcp.toolAccessTitle', { module: mod.display_name })}
      hideActions
      maxWidth="md"
    >
      <ScrollArea className="max-h-[60vh]">
        {isLoading && (
          <p className="text-xs text-muted-foreground py-4 text-center">
            {t('common:app.loading')}
          </p>
        )}

        {!isLoading && (!tools || tools.length === 0) && (
          <p className="text-xs text-muted-foreground py-4 text-center">
            {t('mcp.noToolsDiscovered')}
          </p>
        )}

        {!isLoading && tools && tools.length > 0 && (
          <div className="space-y-4 pb-2">
            {readTools.length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  {t('mcp.accessRead')} ({readTools.length})
                </p>
                <div className="space-y-0.5">
                  {readTools.map((tool) => (
                    <ToolRow
                      key={tool.name}
                      tool={tool}
                      moduleName={mod.name}
                      isDisabled={disabledTools.has(`${mod.name}__${tool.name}`)}
                      onToggle={() => onToggleTool(`${mod.name}__${tool.name}`)}
                      readOnly={readOnly}
                    />
                  ))}
                </div>
              </div>
            )}

            {writeTools.length > 0 && (
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  {t('mcp.accessReadWrite')} ({writeTools.length})
                </p>
                <div className="space-y-0.5">
                  {writeTools.map((tool) => (
                    <ToolRow
                      key={tool.name}
                      tool={tool}
                      moduleName={mod.name}
                      isDisabled={disabledTools.has(`${mod.name}__${tool.name}`)}
                      onToggle={() => onToggleTool(`${mod.name}__${tool.name}`)}
                      readOnly={readOnly}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </DialogForm>
  );
};

/** Single tool row with method badge, name, description tooltip, and on/off switch */
const ToolRow: React.FC<{
  tool: McpTool;
  moduleName: string;
  isDisabled: boolean;
  onToggle: () => void;
  readOnly: boolean;
}> = ({ tool, isDisabled, onToggle, readOnly }) => {
  const displayName = formatToolName(tool.name);
  const method = tool.method?.toUpperCase() || '';
  const description = tool.description?.split('\n').filter(Boolean)[0] || '';

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 py-1 px-2 rounded-md',
        isDisabled && 'opacity-50',
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        {method && (
          <span
            className={cn(
              'text-[9px] font-mono font-bold px-1 py-0.5 rounded shrink-0',
              getMethodColor(method),
            )}
          >
            {method}
          </span>
        )}
        <span className="text-[11px] truncate">{displayName}</span>
        {description && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 text-muted-foreground shrink-0 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-64">
              <p className="text-xs">{description}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <Switch
        checked={!isDisabled}
        onCheckedChange={onToggle}
        disabled={readOnly}
      />
    </div>
  );
};
